import React from 'react'
import { t } from 'i18next'
import { sortBySequenceNumber } from 'shared/lib/common-util'
import TransitPreviewSection from './TransitPreviewSection'
import ConsignmentItemsTable from './ConsignmentItemsTable'
import { HouseConsignment } from '../../house-consignment/models/types'
import { useFetchHouseConsignment } from '../../house-consignment/api/fetchHouseConsignment'
import { useFetchHouseConsignmentDocuments } from '../../house-consignment-document/api/fetchHouseConsignmentDocuments'
import LoadingOverlay from '../../../../components/p5/LoadingOverlay'
import TransitPreviewTransportDocuments from './TransitPreviewTransportDocuments'
import TransitPreviewSupportingDocuments from './TransitPreviewSupportingDocuments'
import TransitPreviewPreviousDocuments from './TransitPreviewPreviousDocuments'
import { isPreviousDocument, isSupportingDocument, isTransportDocument } from '../../document/models/types'
import TransitPreviewDocumentsSection from './TransitPreviewDocumentsSection'
import { useFetchConsignmentItems } from '../../consignment-item/api/fetchConsignmentItems'

interface HouseConsignmentsTableRowDetailsProps {
  consignmentId: number
  houseConsignment: HouseConsignment
}

function HouseConsignmentsTableRowDetails({
  consignmentId,
  houseConsignment,
}: HouseConsignmentsTableRowDetailsProps) {
  const { data: houseConsignmentDetails, isFetching: isHouseConsignmentLoading } = useFetchHouseConsignment(houseConsignment.id)
  const {
    data: houseConsignmentDocumentsData,
    isFetching: isHouseConsignmentDocumentsLoading,
  } = useFetchHouseConsignmentDocuments(houseConsignment.id)

  const { data: consignmentItemsData, isFetching: consignmentItemsLoading } = useFetchConsignmentItems({
    consignmentId,
    houseConsignmentId: houseConsignment.id,
  })

  const houseConsignmentDocuments = houseConsignmentDocumentsData ?? []
  const consignmentItems = consignmentItemsData ?? []

  const initialLoading = isHouseConsignmentLoading || isHouseConsignmentDocumentsLoading || consignmentItemsLoading
  if (!houseConsignmentDetails) {
    return (
      <LoadingOverlay loading>
        <div />
      </LoadingOverlay>
    )
  }

  const transportDocuments = houseConsignmentDocuments.filter(isTransportDocument).sort(sortBySequenceNumber)
  const previousDocuments = houseConsignmentDocuments.filter(isPreviousDocument).sort(sortBySequenceNumber)
  const supportingDocuments = houseConsignmentDocuments.filter(isSupportingDocument).sort(sortBySequenceNumber)

  return (
    <LoadingOverlay loading={initialLoading} minHeight="auto">
      <div className="d-flex flex-wrap">
        <div className="col-6">
          {transportDocuments.length > 0 && (
            <TransitPreviewDocumentsSection>
              <TransitPreviewTransportDocuments transportDocuments={transportDocuments} />
            </TransitPreviewDocumentsSection>
          )}
        </div>
        <div className="col-6">
          {supportingDocuments.length > 0 && (
            <TransitPreviewDocumentsSection>
              <TransitPreviewSupportingDocuments supportingDocuments={supportingDocuments} />
            </TransitPreviewDocumentsSection>
          )}
          {previousDocuments.length > 0 && (
            <TransitPreviewDocumentsSection>
              <TransitPreviewPreviousDocuments previousDocuments={previousDocuments} />
            </TransitPreviewDocumentsSection>
          )}
        </div>
      </div>
      <div className="ps-2">
        <TransitPreviewSection
          columnId="goods"
          headingLabel={`${t('declaration.navbar.goods')} (${houseConsignment.numberOfGoods})`}
          initialSectionState={false}
          delayedOpenSection={houseConsignment.numberOfGoods < 5}
        >
          <ConsignmentItemsTable
            isLoading={consignmentItemsLoading}
            consignmentItemsLoading={consignmentItemsLoading}
            consignmentItems={consignmentItems}
            expectedItemCount={houseConsignment.numberOfGoods}
          />
        </TransitPreviewSection>
      </div>
    </LoadingOverlay>
  )
}

export default HouseConsignmentsTableRowDetails
